import { useMemo } from "react";
import { Order, OrderedProduct } from "src/shared/types";

export type ReverseAmounts = {
  discount: number;
  parcelFee: number;
  deliveryFee: number;
};

type ReturnTotals = {
  qty: number;
  amount: number;
  refund: number;
  adjusted: number;
  remaining: number;
  totalPayment: number;
  adjustmentAmount: number;
  finalAmount: number;
  netReturn: number;
  isFullReturn: boolean;
  reverseDiscount: number;
  reverseParcelFee: number;
  reverseDeliveryFee: number;
};

export const ZERO_REVERSE: ReverseAmounts = { discount: 0, parcelFee: 0, deliveryFee: 0 };

// What the order was originally charged — the cap for each reverse input, and the auto-fill on a full return
export const getReverseCaps = (order: Order | null): ReverseAmounts => ({
  discount: Number(order?.orders?.[0]?.discount ?? 0),
  parcelFee: Number(order?.orders?.[0]?.parcelFee ?? 0),
  deliveryFee: Number(order?.orders?.[0]?.deliveryFee ?? 0),
});

// Quantity per product already sent back by earlier returns
const getReturnedQtyByProduct = (order: Order): Record<string, number> => {
  const returned: Record<string, number> = {};

  (order.orderDetails ?? [])
    .filter((detail) => detail.paymentSource === "RETURN")
    .flatMap((detail) => detail.products ?? [])
    .forEach((p) => {
      returned[p.productId] = (returned[p.productId] ?? 0) + Math.abs(Number(p.quantity || 0));
    });

  return returned;
};

export const useReturnCalculation = (order: Order | null, returnQty: Record<number, number>, reverse: ReverseAmounts): ReturnTotals => {
  return useMemo(() => {
    const empty: ReturnTotals = {
      qty: 0,
      amount: 0,
      refund: 0,
      adjusted: 0,
      remaining: 0,
      totalPayment: 0,
      adjustmentAmount: 0,
      finalAmount: 0,
      netReturn: 0,
      isFullReturn: false,
      reverseDiscount: 0,
      reverseParcelFee: 0,
      reverseDeliveryFee: 0,
    };

    if (!order) return empty;

    // New interface: financial fields in orders[0]
    const transaction = order.orders?.[0];
    const calOfFinalAmount = (order.paymentHistory ?? [])
      .filter((p) => p.paymentSource === "RETURN")
      .reduce(
        (acc, p) =>
          acc +
          Math.abs(Number(p.receivedAmount ?? 0)) + 
          Math.abs(Number((p as any).adjustAmount ?? 0) || 0),
        0
      );
    const finalAmount = Number(transaction?.finalAmount ?? 0) - calOfFinalAmount;

    const checkPayment = (order.paymentHistory ?? []).reduce((acc, p) => acc + Number(p.receivedAmount ?? 0), 0) ?? 0;

    // New interface: receivedAmount replaces ReceivedAmount
    const totalPayment = checkPayment > finalAmount ? finalAmount : checkPayment;

    // New paymentHistory schema has no adjustAmount — default 0
    const adjustmentAmount = 0;

    const remaining = finalAmount - totalPayment - adjustmentAmount;

    // New interface: products in orderDetails[0].products
    const products: OrderedProduct[] = order.orderDetails?.[0]?.products ?? [];

    const alreadyReturned = getReturnedQtyByProduct(order);

    let qty = 0;
    let amount = 0;
    let returnableQty = 0;

    products.forEach((p, i) => {
      const q = returnQty[i] || 0;
      // unitPrice replaces amount
      const price = Number(p.unitPrice ?? 0);
      qty += q;
      amount += q * price;
      returnableQty += Math.max(0, Number(p.quantity ?? 0) - (alreadyReturned[p.productId] ?? 0));
    });

    // Every remaining unit of the order is being sent back
    const isFullReturn = returnableQty > 0 && qty >= returnableQty;

    // Use the reverse values directly from the parameter
    const reverseDiscount = Number(reverse?.discount || 0);
    const reverseParcelFee = Number(reverse?.parcelFee || 0);
    const reverseDeliveryFee = Number(reverse?.deliveryFee || 0);

    // Reversed discount is taken off the return; reversed fees are given back to the customer.
    // Never below 0 — a reversed discount larger than the returned goods is not a charge to the customer.
    const netReturn = Math.max(0, amount - reverseDiscount + reverseParcelFee + reverseDeliveryFee);

    // Only a positive outstanding balance can absorb the return; an advance/overpaid order absorbs nothing
    const adjusted = Math.min(netReturn, Math.max(0, remaining));
    const refund = netReturn - adjusted;

    return {
      qty,
      amount,
      refund,
      adjusted,
      remaining,
      totalPayment,
      adjustmentAmount,
      finalAmount,
      netReturn,
      isFullReturn,
      reverseDiscount,
      reverseParcelFee,
      reverseDeliveryFee,
    };
  }, [order, returnQty, reverse]);
};